import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Button, ButtonGroup, Label } from 'reactstrap';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

import { modelOf } from '../../../prop-types';
import RecurringOrderStore from '../../../store/RecurringOrderStore';
import RecurringCycleDropdown from '../../recurring/RecurringCycleDropdown';
import RecurringOrderProduct from '../../../models/RecurringOrderProduct';
import RecurringDaysDropdown from '../../recurring/RecurringDaysDropdown';
import RecurringOrderCycleType from '../../../types/RecurringOrderCycleType';
import QuantityFieldGroup from '../../common/QuantityFieldGroup';
import { createForm } from '../../../models/Form';
import { createFormField } from '../../../models/FormField';
import { productCountValid, quantityValid } from '../../../util/formValidators';
import globalTranslations from '../../../i18n/globalTranslations';

const MAX_RECURRING_DAYS = 365;
const MAX_QUANTITY = 99999;

function createProductUpdate(id, quantity, cycle, days) {
  return {
    extendedId: id,
    quantity: quantity,
    cycleType: cycle,
    customCycle: days
  };
}

@observer
export class RecurringOrderProductModify extends Component {
  form = null;

  constructor(props) {
    super(props);

    this.state = {
      selectedQuantity: props.product.quantity_ordered,
      selectedCycle: props.product.recurring_order_cycle_type,
      selectedDays: props.product.recurring_order_custom_cycle
        ? props.product.recurring_order_custom_cycle
        : props.minCustomDays
    };

    const quantityStep = props.product.getQuantityStep();

    this.form = createForm({
      quantity: createFormField(
        {
          value: String(quantityStep)
        },
        [
          quantityValid(MAX_QUANTITY, quantityStep),
          productCountValid(quantityStep)
        ]
      )
    });
    this.form.validate();
  }

  handleUpdate = (event) => {
    const { onUpdate, product } = this.props;
    const { selectedQuantity, selectedCycle, selectedDays } = this.state;
    event.preventDefault();

    this.form.validate();
    if (this.form.valid) {
      onUpdate(
        createProductUpdate(
          product.id,
          selectedQuantity,
          selectedCycle,
          selectedDays
        )
      );
    }
  };

  handleRemove = (id) => {
    const { onRemove } = this.props;
    onRemove(id);
  };

  handleQuantityChange = (quantity) => {
    this.setState({
      selectedQuantity: quantity
    });
  };

  handleCycleChange = (event) => {
    const { product, minCustomDays } = this.props;
    const value = event.target.value;
    let selectedDays = product.recurring_order_custom_cycle;

    if (value === RecurringOrderCycleType.CUSTOM && selectedDays === null) {
      selectedDays = minCustomDays;
    }

    this.setState({
      selectedCycle: value,
      selectedDays: selectedDays
    });
  };

  handleDaysChange = (event) => {
    const { minCustomDays } = this.props;
    const value = event.target.value;

    if (minCustomDays <= value && value <= MAX_RECURRING_DAYS) {
      this.setState({
        selectedDays: Number(value)
      });
    }
  };

  render() {
    const { cycles, product, minCustomDays } = this.props;
    const { selectedCycle } = this.state;

    const quantityField = this.form.fields.get('quantity');

    return (
      <div className="RecurringOrderProductModify">
        <div className="RecurringOrderProductModify__quantity">
          <Label for="RecurringOrderProductModify__quantity-label">
            <FormattedMessage
              id="recurringOrder.changeQuantity"
              defaultMessage="Change product quantity"
            />
          </Label>
          <QuantityFieldGroup
            key={product.id}
            className="RecurringOrderProductModify__quantity-group"
            field={quantityField}
            unit={product.stock_unit}
            quantityStep={product.getQuantityStep()}
            onUpdateProductQuantity={this.handleQuantityChange}
            defaultValue={product.quantity_ordered}
            maxQuantity={MAX_QUANTITY}
          />
        </div>
        <div className="RecurringOrderProductModify__cycle">
          <RecurringCycleDropdown
            cycles={cycles}
            onChange={this.handleCycleChange}
            selectedValue={product.recurring_order_cycle_type}
          />
        </div>
        {minCustomDays &&
          selectedCycle === RecurringOrderCycleType.CUSTOM && (
            <div className="RecurringOrderProductModify__days">
              <RecurringDaysDropdown
                onChange={this.handleDaysChange}
                selectedValue={this.state.selectedDays}
              />
            </div>
          )}
        <ButtonGroup className="RecurringOrderProductModify__item-buttons">
          <Button onClick={this.handleUpdate}>
            <FormattedMessage {...globalTranslations.saveTitle} />
          </Button>
          <Button
            onClick={() =>
              window.confirm(
                this.props.intl.formatMessage(
                  globalTranslations.confirmRemoveTitle
                )
              ) && this.handleRemove(product.id)
            }
            color="danger"
          >
            <FormattedMessage {...globalTranslations.removeTitle} />
          </Button>
        </ButtonGroup>
      </div>
    );
  }
}

RecurringOrderProductModify.propTypes = {
  recurringOrderStore: modelOf(RecurringOrderStore).isRequired,
  product: modelOf(RecurringOrderProduct).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  cycles: PropTypes.object,
  minCustomDays: PropTypes.number
};

export default injectIntl(
  inject('currencyStore', 'recurringOrderStore')(RecurringOrderProductModify)
);



// WEBPACK FOOTER //
// ./src/components/order/RecurringOrderProductModify/index.jsx